import { Link } from '@tanstack/react-router';
import moment from 'moment';
import { useRef } from 'react';

import { useIntersectionObserver, useLoadMoreBackoff } from '@common/hooks';
import { useAppStore } from '@common/stores/useAppStore';
import { Loader } from '@common/ui/Loader';
import { Result } from '@common/ui/Result';
import { Separator } from '@common/ui/Separator';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { ChatCell } from '@entities/chat/ui/ChatCell';
import { ChatProfilePicture } from '@entities/chat/ui/ChatProfilePicture';
import { ScrollChatImageProfiles } from '@entities/chat/ui/ChatScrollImageProfiles/ScrollChatImageProfiles';

import { refreshMatchesStore } from '@services/StoreRefreshService';

export const ChatList: React.FC = () => {
  const store = useAppStore();
  const { getSortedMatches, matchesMeta } = useAppStore();
  const auth = useAuthStore();
  const currentUserId = auth?.user?.id;

  // Use sorted matches instead of raw Map values
  const matchesArray = getSortedMatches();

  const hasMore = matchesMeta?.pagination?.hasMore;
  const isLoadingMore = matchesMeta?.state === 'loadingMore';
  const isLoading = matchesMeta?.state === 'loading';

  // A failed page is not requested again until its back-off ends.
  const { isBackingOff, canLoadMore, onLoadFailure, onLoadSuccess } =
    useLoadMoreBackoff();

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useIntersectionObserver({
    root: scrollContainerRef.current,
    rootMargin: '100px',
    threshold: 0.1,
    enabled:
      hasMore &&
      !isLoadingMore &&
      !isLoading &&
      !isBackingOff &&
      matchesArray.length > 0,
    onIntersect: () => {
      if (!canLoadMore()) return;

      refreshMatchesStore(
        store,
        {
          limit: 10,
          after: matchesMeta.pagination?.nextCursor as string,
        },
        { onSuccess: onLoadSuccess, onError: onLoadFailure },
      );
    },
  });

  if (isLoading) {
    return <Loader />;
  }

  if (matchesArray.length === 0)
    return (
      <Result
        src={'/icons/3d-icons/message.png'}
        title="Здесь будут чаты"
        description="Когда вам напишут"
        iconContainerBg="#FF5C8C12"
      />
    );

  return (
    <>
      <ScrollChatImageProfiles>
        {matchesArray.map((user, index) => (
          <Link
            to="/chats/$matchId"
            params={{ matchId: user.match_id }}
            key={index}
          >
            <ChatProfilePicture
              image={user.photos[0]}
              online={
                !user.last_message ||
                (!user?.last_message.is_read &&
                  user.last_message.sender_id !== currentUserId)
              }
            />
          </Link>
        ))}
      </ScrollChatImageProfiles>
      <Separator style={{ marginTop: -10 }} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '0 16px',
        }}
      >
        {matchesArray.map((match, index) => (
          <Link
            to="/chats/$matchId"
            params={{ matchId: match.match_id }}
            key={index}
          >
            <ChatCell
              key={index}
              type={
                match.last_message?.sender_id === currentUserId
                  ? 'my'
                  : 'friend'
              }
              status={
                match.last_message
                  ? match.last_message.is_read
                    ? 'readed'
                    : 'sended'
                  : undefined
              }
              lastMessageDate={
                match.last_message_date
                  ? moment(match.last_message_date)
                  : undefined
              }
              text={match.last_message?.content}
              unreadedMsg={
                match.is_unread && match.unread_count === 0
                  ? 1
                  : match.unread_count
              }
              profile={match}
            />
          </Link>
        ))}
        {hasMore && (
          <div ref={topSentinelRef} style={{ height: '1px', flexShrink: 0 }} />
        )}

        {isLoadingMore && <Loader style={{ margin: '0 auto' }} key={0} />}
      </div>
    </>
  );
};

ChatList.displayName = 'ChatList';
